import { DateTime } from 'luxon'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { MARKET_TIMEZONE } from '@/features/bidding/constants'
import type { ProcessedForecastRow } from '@/features/bidding/types/forecastPricing'

/**
 * Get the market timezone string.
 * Currently hardcoded to Europe/Paris (CET/CEST).
 * Can be extended to accept market-specific timezones in the future.
 */
export function getMarketTimezone(): string {
  return MARKET_TIMEZONE.name
}

/**
 * Convert a single forecast row from UTC to market timezone.
 * Extracts quantile values and computes the hour in market timezone.
 *
 * @param row - Raw forecast row from Databricks API
 * @param marketTimezone - IANA timezone string (e.g., 'Europe/Paris')
 * @returns Processed row with market timezone DateTime and extracted quantiles
 */
export function convertForecastRowToMarketTimezone(
  row: FormattedRow,
  marketTimezone: string,
): ProcessedForecastRow | null {
  const dsValue = row.ds
  if (typeof dsValue !== 'string') {
    return null
  }

  // Parse UTC timestamp and convert to market timezone
  const utcDateTime = DateTime.fromISO(dsValue, { zone: 'utc' })
  if (!utcDateTime.isValid) {
    return null
  }

  const marketDateTime = utcDateTime.setZone(marketTimezone)
  const isoDate = marketDateTime.toISODate()
  if (isoDate === null) {
    return null
  }

  // Extract quantile values, defaulting to NaN if invalid
  const p10 = Number(row['forecast-lo-80'])
  const p25 = Number(row['forecast-lo-50'])
  const mean = Number(row['mean'])
  const p75 = Number(row['forecast-hi-50'])
  const p90 = Number(row['forecast-hi-80'])

  // Skip rows with invalid price data
  if ([p10, p25, mean, p75, p90].some((v) => Number.isNaN(v))) {
    return null
  }

  return {
    marketDateTime,
    hour: marketDateTime.hour,
    date: isoDate,
    p10,
    p25,
    mean,
    p75,
    p90,
  }
}

/**
 * Convert an array of forecast rows to market timezone.
 * Filters out invalid rows.
 *
 * @param rows - Raw forecast rows from Databricks API
 * @param marketTimezone - IANA timezone string (e.g., 'Europe/Paris')
 * @returns Array of processed rows with market timezone data
 */
export function convertForecastsToMarketTimezone(rows: FormattedRow[], marketTimezone: string): ProcessedForecastRow[] {
  const processed: ProcessedForecastRow[] = []

  for (const row of rows) {
    const converted = convertForecastRowToMarketTimezone(row, marketTimezone)
    if (converted !== null) {
      processed.push(converted)
    }
  }

  return processed
}

/**
 * Get the number of hours in a specific day for a given timezone.
 * Returns 23 for spring DST transition, 25 for fall DST transition, 24 otherwise.
 *
 * @param date - DateTime object representing the day
 * @param timezone - IANA timezone string
 * @returns Number of hours in the day (23, 24, or 25)
 */
export function getDayHoursInTimezone(date: DateTime, timezone: string): number {
  const startOfDay = date.setZone(timezone).startOf('day')
  const startOfNextDay = startOfDay.plus({ days: 1 })
  const diff = startOfNextDay.diff(startOfDay, 'hours')
  return Math.round(diff.hours)
}
